import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  UserCircleIcon,
  PhoneIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  BanknotesIcon,
  CalendarIcon,
  BriefcaseIcon,
  MapPinIcon,
  DocumentCheckIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import ReviewerNameModal from '../candidateReview/ReviewerNameModal';

// Per link, so a forwarded link on a shared machine still asks the next person their name.
const FILLER_NAME_KEY = 'armSalaryFillerName:';

function readStoredName(token) {
  try {
    return (window.localStorage.getItem(FILLER_NAME_KEY + token) || '').trim();
  } catch {
    return '';
  }
}

function storeName(token, name) {
  try {
    window.localStorage.setItem(FILLER_NAME_KEY + token, name);
  } catch {
    // Private mode / blocked storage: the name lives in state for this visit only.
  }
}

// Tells the server who opened the link (it only logs it). Best effort: never blocks the form.
function reportViewer(token, name) {
  axios.post(`/api/arm-salary/fill/${token}/viewer`, { name }).catch(() => {});
}

export default function ArmSalaryForm() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expired, setExpired] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [reqData, setReqData] = useState(null);

  // Form fields
  const [doj, setDoj] = useState('');
  const [grossSalary, setGrossSalary] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fillerName, setFillerName] = useState(() => readStoredName(token));
  const [nameModal, setNameModal] = useState(null); // null | 'ask' | 'edit'

  useEffect(() => {
    if (!token) {
      setErrorMessage('Missing token');
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/arm-salary/fill/${token}`);
        if (res.data.success) {
          const req = res.data.request;
          setReqData(req);
          setExpired(!!res.data.expired);
          if (req.doj) {
            const d = new Date(req.doj);
            if (!isNaN(d.getTime())) {
              setDoj(d.toISOString().split('T')[0]);
            }
          }
          if (req.grossSalary) setGrossSalary(req.grossSalary);
          if (req.status === 'completed') {
            setIsSubmitted(true);
          }
          if (!res.data.expired) {
            const remembered = readStoredName(token);
            if (remembered) reportViewer(token, remembered);
            else setNameModal('ask');
          }
        }
      } catch (err) {
        console.error('Failed to load salary request:', err);
        if (err.response?.status === 404) {
          setErrorMessage('Invalid or expired link. Please contact the TalentCrux recruiter.');
        } else if (err.response?.data?.expired) {
          setExpired(true);
        } else {
          setErrorMessage(err.response?.data?.message || 'Failed to load details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fillerName) {
      setNameModal('ask');
      return;
    }
    if (!doj) {
      toast.error('Please select the Date of Joining (DOJ)');
      return;
    }
    if (!grossSalary.trim()) {
      toast.error('Please enter the Gross Salary');
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.post(`/api/arm-salary/fill/${token}`, {
        doj,
        grossSalary: grossSalary.trim(),
        filledByName: fillerName,
      });

      if (res.data.success) {
        setIsSubmitted(true);
        setReqData(res.data.request);
        toast.success('Salary and onboarding details saved successfully!');
      }
    } catch (err) {
      console.error('Failed to submit salary details:', err);
      toast.error(err.response?.data?.message || 'Failed to save salary details');
    } finally {
      setSubmitting(false);
    }
  };

  const snapshot = reqData?.candidateSnapshot || {};

  const handleNameSubmit = (name) => {
    setFillerName(name);
    storeName(token, name);
    setNameModal(null);
    reportViewer(token, name);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-600 font-medium">Loading candidate details...</p>
        </div>
      </div>
    );
  }

  if (errorMessage || expired) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-slate-100 text-center">
          <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExclamationTriangleIcon className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">
            {expired ? 'Link Expired' : 'Unable to Access Link'}
          </h2>
          <p className="text-slate-600 text-sm mb-6">
            {expired
              ? 'This salary details request link has expired (validity is 30 days). Please contact the HR/Recruiting team to request a new link.'
              : errorMessage}
          </p>
          <div className="p-3 bg-slate-50 rounded-xl text-xs text-slate-500 flex items-center justify-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-slate-400" />
            TalentCrux Secure Candidate Onboarding Portal
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-50 py-10 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Top Header Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 h-32 w-32 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-bl-full pointer-events-none" />
          
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                  <BuildingOffice2Icon className="w-3.5 h-3.5" />
                  Regional Head / HRBP Action
                </span>
                {isSubmitted && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                    Completed
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                Candidate Salary &amp; Onboarding Details
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                Please review the candidate's screening information and provide the required compensation and joining details.
              </p>
            </div>
          </div>
        </div>

        {/* Candidate Information Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
          <div className="flex items-center gap-2 pb-4 mb-5 border-b border-slate-100">
            <UserCircleIcon className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-bold text-slate-800">Selected Candidate Profile</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-xs text-slate-400 font-medium block mb-0.5">Candidate Name</span>
              <span className="font-semibold text-slate-800 text-base">{snapshot.name || '—'}</span>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100">
              <span className="text-xs text-slate-400 font-medium block mb-0.5">Position / Role</span>
              <span className="font-semibold text-slate-800 text-base">{reqData?.jobTitle || 'Open Position'}</span>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
              <PhoneIcon className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <span className="text-xs text-slate-400 font-medium block">Mobile Phone</span>
                <span className="font-medium text-slate-700">{snapshot.phone || '—'}</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
              <EnvelopeIcon className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <span className="text-xs text-slate-400 font-medium block">Email Address</span>
                <span className="font-medium text-slate-700">{snapshot.email || '—'}</span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
              <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <span className="text-xs text-slate-400 font-medium block">Store Code &amp; Name</span>
                <span className="font-semibold text-slate-800">
                  {snapshot.storeCode ? `${snapshot.storeCode}${snapshot.storeName ? ` - ${snapshot.storeName}` : ''}` : '—'}
                </span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center gap-3">
              <BuildingOffice2Icon className="w-4 h-4 text-slate-400 shrink-0" />
              <div>
                <span className="text-xs text-slate-400 font-medium block">ARM Name &amp; Region</span>
                <span className="font-medium text-slate-700">
                  {snapshot.armName || '—'} {snapshot.region ? `(${snapshot.region})` : ''}
                </span>
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-400 font-medium block mb-0.5">Interview Score</span>
                <span className="font-semibold text-slate-800">
                  {snapshot.interviewScore != null ? `${snapshot.interviewScore}%` : 'N/A'}
                </span>
              </div>
              {snapshot.interviewScore != null && (
                <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                  {snapshot.interviewScore}%
                </span>
              )}
            </div>

            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-100 flex items-center justify-between">
              <div>
                <span className="text-xs text-slate-400 font-medium block mb-0.5">Document Status</span>
                <span className="font-semibold text-slate-800">{snapshot.documentsStatus || 'Pending'}</span>
              </div>
              <DocumentCheckIcon className="w-5 h-5 text-slate-400" />
            </div>
          </div>
        </div>

        {/* Input Form Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
          <div className="flex items-center justify-between pb-4 mb-6 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <BanknotesIcon className="w-5 h-5 text-emerald-600" />
              <h2 className="text-lg font-bold text-slate-800">
                {isSubmitted ? 'Submitted Onboarding Details' : 'Enter Salary Details'}
              </h2>
            </div>
            {isSubmitted && (
              <span className="text-xs text-slate-400">
                Completed on {reqData?.completedAt ? new Date(reqData.completedAt).toLocaleDateString() : '—'}
              </span>
            )}
          </div>

          {isSubmitted && (
            <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-800">
                <p className="font-semibold mb-0.5">Details Submitted Successfully</p>
                <p className="text-xs text-emerald-700">
                  These details are reflected in the recruiter's Selected Candidates table. You can update the values below at any time if adjustments are needed.
                </p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="doj" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Date of Joining (DOJ) <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <CalendarIcon className="w-5 h-5" />
                  </div>
                  <input
                    id="doj"
                    type="date"
                    required
                    value={doj}
                    onChange={(e) => setDoj(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-800 transition"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="grossSalary" className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-1.5">
                  Gross Salary <span className="text-rose-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <BanknotesIcon className="w-5 h-5" />
                  </div>
                  <input
                    id="grossSalary"
                    type="number"
                    required
                    value={grossSalary}
                    onChange={(e) => setGrossSalary(e.target.value)}
                    placeholder="e.g. 22000"
                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-slate-800 placeholder-slate-400 transition"
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-center justify-between gap-3">
              <span className="text-xs text-slate-400 flex items-center gap-1.5">
                <ClockIcon className="w-4 h-4" />
                {fillerName ? (
                  <>
                    Filling as <span className="font-semibold text-slate-600">{fillerName}</span>
                    <button
                      type="button"
                      onClick={() => setNameModal('edit')}
                      className="text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      Change
                    </button>
                  </>
                ) : (
                  'Submitted data is logged securely with audit history'
                )}
              </span>

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-sm rounded-xl shadow-sm transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : isSubmitted ? (
                  <span>Update Details</span>
                ) : (
                  <span>Submit Details</span>
                )}
              </button>
            </div>
          </form>
        </div>

        <ReviewerNameModal
          isOpen={!!nameModal}
          initialName={nameModal === 'edit' ? fillerName : ''}
          isRequired={nameModal === 'ask'}
          title="What is your name?"
          description="Your name is recorded with the salary and joining details you submit, so the hiring team knows who filled them."
          onSubmit={handleNameSubmit}
          onCancel={() => setNameModal(null)}
        />

        {/* Footer info */}
        <div className="text-center text-xs text-slate-400 py-4">
          Powered by TalentCrux AI Hiring Intelligence Platform &bull; Secure Regional Head Action Link
        </div>
      </div>
    </div>
  );
}
